import type { GridRowId } from "@mui/x-data-grid";
import { useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Timestamp, doc, getDoc, writeBatch } from "firebase/firestore";
import { database } from "../../firebase";
import { createEnvio } from "../../firebase/firestore/envios";
import { usePaqueteStore } from "../../zustand/usePaquetesStore";
import { useDateRangeStore } from "../../zustand/useDateRangeStore";
import { useAgenciaStore } from "../../zustand/useAgenciaStore";
import dayjs from "dayjs";

export default function FormCreateShipment({
  onClose,
  ids,
}: {
  onClose: () => void;
  ids?: Set<GridRowId>;
}) {
  const [nombreEnvio, setNombreEnvio] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { fetchAllPaquetes, fetchCounts } = usePaqueteStore();
  const { fechaInicio, fechaFin } = useDateRangeStore();
  const { agencia } = useAgenciaStore();
  const theme = useTheme();

  const handleCreateShipment = async () => {
    if (!ids || ids.size === 0) {
      toast.error("Debe seleccionar al menos un paquete");
      return;
    }

    if (!nombreEnvio.trim()) {
      toast.error("Debe ingresar un nombre para el envío");
      return;
    }

    setIsLoading(true);

    try {
      const idsString = Array.from(ids).map(String);
      const cantidadPaquetes = idsString.length;

      // Crear el envío
      await createEnvio({
        nombre: nombreEnvio.trim(),
        cantidadPaquetes,
        fechaDesde: Timestamp.fromDate(fechaInicio.startOf("day").toDate()),
        fechaHasta: Timestamp.fromDate(fechaFin.endOf("day").toDate()),
        estado: agencia?.estados?.[0]?.id || "recibido",
      });

      // Actualizar los paquetes con el nombre del envío
      const batch = writeBatch(database);
      
      for (const id of idsString) {
        const paqueteRef = doc(database, "paquetes", id);
        const paqueteSnap = await getDoc(paqueteRef);
        
        if (paqueteSnap.exists()) {
          batch.update(paqueteRef, {
            envio: nombreEnvio.trim(),
          });
        }
      }

      await batch.commit();

      toast.success(`Envío "${nombreEnvio}" creado con éxito con ${cantidadPaquetes} paquete(s)`);
      
      await Promise.all([fetchAllPaquetes(), fetchCounts()]);
      onClose();
    } catch (error) {
      console.error("Error creando envío:", error);
      toast.error("Error al crear el envío. Intente nuevamente");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Divider />
      <Box display="flex" flexDirection="column" gap={2} mt={2} px={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          Información del envío
        </Typography>

        <TextField
          label="Nombre del envío"
          value={nombreEnvio}
          onChange={(e) => setNombreEnvio(e.target.value)}
          fullWidth
          required
          placeholder="Ej: Envío Miami - Enero 2024"
        />

        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            padding: 2,
            backgroundColor: theme.palette.mode === "dark" 
              ? theme.palette.background.paper 
              : theme.palette.action.hover,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Cantidad de paquetes:</strong> {ids?.size || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Corte:</strong> {dayjs(fechaInicio).format("DD/MM/YYYY")} - {dayjs(fechaFin).format("DD/MM/YYYY")}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ marginTop: 2 }} />
      <Box display="flex" gap={2} p={2}>
        <Button onClick={onClose} sx={{ mr: 1 }}>
          Cancelar
        </Button>
        <Box sx={{ flex: "1 1 auto" }} />
        <Button
          onClick={handleCreateShipment}
          variant="contained"
          disabled={isLoading || !nombreEnvio.trim() || !ids || ids.size === 0}
        >
          {isLoading ? "Creando..." : "Crear envío"}
        </Button>
      </Box>
    </>
  );
}
